// ============================================================
// frontend/src/pages/FacturesPro.js
// Module de réception des factures d'achat via Peppol / Peppyrus
// Accessible : admin uniquement
// ============================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import theme from '../theme';
import { useIsMobile } from '../hooks/useIsMobile';

const API_URL     = process.env.REACT_APP_API_URL     || 'http://localhost:5000/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// ── Catégories TVA belge — législation Art. 45-48 CTVA ───────────────────────
const CATEGORIES_TVA = {
  standard:       { label: 'Standard',            icon: '📄', pct: 100, color: '#27ae60', bg: '#e8f5e9', loi: '100% déductible'           },
  vehicule:       { label: 'Véhicule',             icon: '🚗', pct: 50,  color: '#f39c12', bg: '#fff3e0', loi: 'Art. 45§2 — max 50%'       },
  carburant:      { label: 'Carburant',            icon: '⛽', pct: 50,  color: '#f39c12', bg: '#fff3e0', loi: 'Art. 45§2 — max 50%'       },
  restaurant:     { label: 'Restaurant / Repas',   icon: '🍽️', pct: 0,   color: '#e74c3c', bg: '#fef2f2', loi: 'Art. 45§3,3° — 0%'        },
  assurance:      { label: 'Assurance',            icon: '🛡️', pct: 0,   color: '#9b59b6', bg: '#f3e5f5', loi: 'Art. 44§3,4° — exonérée'  },
  hotel:          { label: 'Hébergement',          icon: '🏨', pct: 100, color: '#27ae60', bg: '#e8f5e9', loi: '100% déductible (pro)'     },
  cadeau_affaire: { label: 'Cadeau d\'affaires',   icon: '🎁', pct: 0,   color: '#e74c3c', bg: '#fef2f2', loi: 'Art. 45§1,4° — 0%'        },
  telecom:        { label: 'Télécom / IT',         icon: '📡', pct: 100, color: '#27ae60', bg: '#e8f5e9', loi: '100% déductible'           },
  fournitures:    { label: 'Fournitures',          icon: '📦', pct: 100, color: '#27ae60', bg: '#e8f5e9', loi: '100% déductible'           },
  autre:          { label: 'Autre / À qualifier',  icon: '❓', pct: 100, color: '#7f8c8d', bg: '#ecf0f1', loi: 'À vérifier'               },
};

const EMPTY_ADD_FORM = {
  nom_fournisseur: '', numero_tva_fournisseur: '', email_fournisseur: '',
  adresse_fournisseur: '', iban_fournisseur: '',
  numero_facture_fournisseur: '', reference_commande: '',
  date_emission: new Date().toISOString().split('T')[0],
  date_echeance: '',
  montant_ttc: '', montant_ht: '', montant_tva: '', taux_tva: '21',
  categorie_tva: 'standard', tva_deductible_pct: 100,
  deduction_majoree_120: false,
  notes_admin: '',
  pdf_path: '', pdf_filename: '', ocr_status: 'manual',
};

// ── Styles ───────────────────────────────────────────────────────────────────
const S = {
  btn: {
    border: 'none', borderRadius: '6px', cursor: 'pointer',
    fontWeight: '600', fontSize: '12px', padding: '6px 12px',
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    transition: 'opacity .15s',
  },
  card: {
    background: 'white', borderRadius: '12px',
    boxShadow: theme.shadow, overflow: 'hidden',
  },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    background: 'white', borderRadius: '14px', padding: '28px',
    maxWidth: '780px', width: '96%', maxHeight: '90vh',
    overflowY: 'auto', boxShadow: theme.shadowLg,
  },
};

// ── Statuts ──────────────────────────────────────────────────────────────────
const STATUTS = {
  recue:              { label: '📬 Reçue',           bg: theme.infoBg,     color: theme.info    },
  validee:            { label: '✅ Validée',          bg: theme.successBg,  color: theme.success },
  en_attente_paiement:{ label: '⏳ À payer',          bg: '#fff8e1',        color: '#f57f17'     },
  payee:              { label: '💚 Payée',            bg: theme.successBg,  color: theme.success },
  rejetee:            { label: '❌ Rejetée',          bg: theme.dangerBg,   color: theme.danger  },
  doublon:            { label: '⚠️ Doublon',          bg: '#f3e5f5',        color: '#7b1fa2'     },
};

const StatutBadge = ({ statut }) => {
  const cfg = STATUTS[statut] || STATUTS.recue;
  return (
    <span style={{ background: cfg.bg, color: cfg.color, padding: '3px 11px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap' }}>
      {cfg.label}
    </span>
  );
};

// ── Formatage montant ─────────────────────────────────────────────────────────
const eur = (n) => new Intl.NumberFormat('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(parseFloat(n || 0));

// ── Formatage date ────────────────────────────────────────────────────────────
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('fr-BE'); } catch { return d; }
};

// Retourne {annee, trimestre} d'une date ISO (YYYY-MM-DD), ou null si invalide/future
function getQuarterFromDate(dateStr) {
  if (!dateStr || dateStr.length < 10) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const annee     = d.getFullYear();
  const trimestre = Math.floor(d.getMonth() / 3) + 1;
  // Ne vérifier que les trimestres passés (le trimestre en cours n'est jamais verrouillé)
  const endOfQ = new Date(annee, trimestre * 3, 1);
  if (endOfQ > new Date()) return null;
  return { annee, trimestre };
}

// ── Card statistique ──────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, color, sub, wrapStyle }) => (
  <div style={{ background: 'white', borderRadius: '12px', padding: '18px 20px', boxShadow: theme.shadow, borderLeft: `4px solid ${color}`, ...wrapStyle }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <span style={{ fontSize: '28px' }}>{icon}</span>
      <div>
        <div style={{ fontSize: '22px', fontWeight: '700', color }}>{value}</div>
        <div style={{ fontSize: '12px', color: theme.textMuted }}>{label}</div>
        {sub && <div style={{ fontSize: '11px', color: theme.textMuted, marginTop: '2px' }}>{sub}</div>}
      </div>
    </div>
  </div>
);

// ═════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═════════════════════════════════════════════════════════════════════════════
const FacturesPro = () => {
  const isMobile = useIsMobile();
  const [factures,       setFactures]       = useState([]);
  const [stats,          setStats]          = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [syncing,        setSyncing]        = useState(false);
  const [syncResult,     setSyncResult]     = useState(null); // { nouvelles, doublons }
  const [filtreStatut,   setFiltreStatut]   = useState('tous');
  const [search,         setSearch]         = useState('');
  const [periodeType,    setPeriodeType]    = useState('tout');   // 'tout' | 'mois' | 'trimestre'
  const [periodeAnnee,   setPeriodeAnnee]   = useState(new Date().getFullYear());
  const [periodeMois,    setPeriodeMois]    = useState(new Date().getMonth());      // 0-11
  const [periodeQ,       setPeriodeQ]       = useState(Math.floor(new Date().getMonth() / 3)); // 0-3
  const [selected,       setSelected]       = useState(null); // Facture sélectionnée pour modal
  const [showModal,      setShowModal]      = useState(false);
  const [showXML,        setShowXML]        = useState(false);
  const [xmlContent,     setXmlContent]     = useState('');
  const [showPDF,        setShowPDF]        = useState(false);
  const [pdfUrl,         setPdfUrl]         = useState(null);
  const [actionLoading,  setActionLoading]  = useState(false);
  const [motifRejet,     setMotifRejet]     = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importId,        setImportId]        = useState('');
  const [importLoading,   setImportLoading]   = useState(false);
  const [importResult,    setImportResult]    = useState(null);
  const [inboxPeppyrus,   setInboxPeppyrus]   = useState(null);  // null = pas chargé, [] = vide
  const [inboxLoading,    setInboxLoading]    = useState(false);

  // ── Mode édition modal détail ────────────────────────────────────────────
  const [editMode,   setEditMode]   = useState(false);
  const [editForm,   setEditForm]   = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editToast,  setEditToast]  = useState(null);

  // ── Modal "Ajouter facture d'achat" ─────────────────────────────────────
  const [showAddModal,   setShowAddModal]   = useState(false);
  const [addMode,        setAddMode]        = useState('manuel'); // 'manuel' | 'ocr'
  const [addForm,        setAddForm]        = useState(EMPTY_ADD_FORM);
  const [addOcrLoading,  setAddOcrLoading]  = useState(false);
  const [addOcrText,     setAddOcrText]     = useState('');
  const [addPreview,     setAddPreview]     = useState(null);
  const [addSaving,      setAddSaving]      = useState(false);
  const [addToast,       setAddToast]       = useState(null);
  const [tvaLockWarning, setTvaLockWarning] = useState(null); // { annee, trimestre } si trimestre verrouillé
  const addFileRef = useRef();

  const token  = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  // Vérifie si la date saisie tombe dans un trimestre TVA verrouillé
  const checkTvaLock = useCallback(async (dateStr) => {
    const q = getQuarterFromDate(dateStr);
    if (!q) { setTvaLockWarning(null); return; }
    try {
      const tk = localStorage.getItem('token');
      const r  = await axios.get(
        `${API_URL}/tva/trimestre-statut?annee=${q.annee}&trimestre=${q.trimestre}`,
        { headers: { Authorization: `Bearer ${tk}` } }
      );
      setTvaLockWarning(r.data.statut === 'verrouille' ? q : null);
    } catch { setTvaLockWarning(null); }
  }, []);

  // Efface l'avertissement dès que la modal se ferme
  useEffect(() => { if (!showAddModal) setTvaLockWarning(null); }, [showAddModal]);

  // ── Chargement initial (DB seulement, pas de sync auto) ──────────────────
  const fetchFromDB = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtreStatut !== 'tous') params.set('statut', filtreStatut);
      if (search)                   params.set('search', search);

      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/facturespro?${params}`, config),
        axios.get(`${API_URL}/facturespro/stats`, config),
      ]);
      setFactures(listRes.data.data || []);
      setStats(statsRes.data.data   || null);
    } catch (err) {
      console.error('[FacturesPro]', err.message);
    } finally {
      setLoading(false);
    }
  }, [filtreStatut, search]);

  useEffect(() => { fetchFromDB(); }, [fetchFromDB]);

  // ── Helpers modal édition ────────────────────────────────────────────────
  const startEdit = () => {
    if (!selected) return;
    setEditForm({
      nom_fournisseur:            selected.nom_fournisseur            || '',
      numero_tva_fournisseur:     selected.numero_tva_fournisseur     || '',
      email_fournisseur:          selected.email_fournisseur          || '',
      adresse_fournisseur:        selected.adresse_fournisseur        || '',
      iban_fournisseur:           selected.iban_fournisseur           || '',
      numero_facture_fournisseur: selected.numero_facture_fournisseur || '',
      date_emission:              selected.date_emission              ? selected.date_emission.slice(0, 10) : '',
      date_echeance:              selected.date_echeance              ? selected.date_echeance.slice(0, 10) : '',
      montant_ht:   selected.montant_ht  != null ? String(selected.montant_ht)  : '',
      montant_tva:  selected.montant_tva != null ? String(selected.montant_tva) : '',
      montant_ttc:  selected.montant_ttc != null ? String(selected.montant_ttc) : '',
      taux_tva:     selected.taux_tva    != null ? String(selected.taux_tva)    : '21',
      notes_admin:  selected.notes_admin || '',
    });
    setEditMode(true);
    setEditToast(null);
  };

  const cancelEdit = () => { setEditMode(false); setEditToast(null); };

  const saveEdit = async () => {
    if (!editForm.nom_fournisseur?.trim()) {
      setEditToast({ msg: 'Le nom du fournisseur est obligatoire.', type: 'danger' }); return;
    }
    if (!editForm.montant_ttc || parseFloat(editForm.montant_ttc) === 0) {
      setEditToast({ msg: 'Le montant TTC est obligatoire.', type: 'danger' }); return;
    }
    setEditSaving(true);
    try {
      const r = await axios.put(`${API_URL}/facturespro/${selected.id_facture_achat}`, editForm, config);
      const updated = r.data.data;
      setFactures(prev => prev.map(f => f.id_facture_achat === updated.id_facture_achat ? updated : f));
      setSelected(updated);
      setEditMode(false);
      setEditToast({ msg: '✅ Facture mise à jour.', type: 'success' });
      setTimeout(() => setEditToast(null), 3000);
    } catch (e) {
      setEditToast({ msg: '❌ ' + (e.response?.data?.message || e.message), type: 'danger' });
    }
    setEditSaving(false);
  };

  // ── Helpers modal Ajouter ────────────────────────────────────────────────
  const showAddToast = (msg, type = 'success') => {
    setAddToast({ msg, type });
    setTimeout(() => setAddToast(null), 4000);
  };

  const calcFromTTC = (ttc, tauxStr) => {
    const taux = parseFloat(tauxStr || 21);
    const ht   = ttc / (1 + taux / 100);
    const tva  = ttc - ht;
    return { ht: ht.toFixed(2), tva: tva.toFixed(2) };
  };

  const handleOcrFacture = async (file) => {
    if (!file) return;
    setAddOcrLoading(true);
    setAddOcrText('');
    setAddPreview(URL.createObjectURL(file));
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API_URL}/facturespro/ocr`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const d = r.data?.data || {};
      if (d.ocr_text) setAddOcrText(d.ocr_text);
      const ocrOk = d.ocr_status === 'done';
      setAddForm(prev => ({
        ...prev,
        // Si l'OCR ne trouve pas le nom, mettre 'À identifier' pour ne pas bloquer la sauvegarde
        nom_fournisseur:            d.nom_fournisseur            || prev.nom_fournisseur || (ocrOk ? 'À identifier' : prev.nom_fournisseur),
        numero_tva_fournisseur:     d.numero_tva_fournisseur     || prev.numero_tva_fournisseur,
        numero_facture_fournisseur: d.numero_facture_fournisseur || prev.numero_facture_fournisseur,
        iban_fournisseur:           d.iban_fournisseur           || prev.iban_fournisseur,
        date_emission:              d.date_emission              || prev.date_emission,
        date_echeance:              d.date_echeance              || prev.date_echeance,
        montant_ht:  d.montant_ht  !== '' ? d.montant_ht  : prev.montant_ht,
        montant_tva: d.montant_tva !== '' ? d.montant_tva : prev.montant_tva,
        montant_ttc: d.montant_ttc !== '' ? d.montant_ttc : prev.montant_ttc,
        taux_tva:    d.taux_tva    || prev.taux_tva,
        pdf_path:    d.pdf_path    || prev.pdf_path,
        pdf_filename: d.pdf_filename || prev.pdf_filename,
        ocr_status:  d.ocr_status  || 'done',
      }));
      if (ocrOk) {
        showAddToast('✅ Fichier analysé — vérifiez et complétez les champs si nécessaire.', 'success');
      } else {
        showAddToast('⚠️ Analyse partielle — le fichier est enregistré, complétez les champs manuellement.', 'warning');
      }
    } catch (e) {
      showAddToast('❌ Erreur analyse : ' + (e.response?.data?.message || e.message) + ' — complétez manuellement.', 'danger');
    }
    setAddOcrLoading(false);
  };

  const saveManual = async () => {
    if (!addForm.nom_fournisseur.trim()) { showAddToast('Le nom du fournisseur est obligatoire.', 'danger'); return; }
    if (!addForm.montant_ttc || parseFloat(addForm.montant_ttc) === 0) { showAddToast('Le montant TTC est obligatoire.', 'danger'); return; }
    setAddSaving(true);
    try {
      const fd = new FormData();
      Object.entries(addForm).forEach(([k, v]) => { if (v !== '' && v != null) fd.append(k, v); });
      fd.set('origine', addMode === 'ocr' ? 'ocr' : 'manuel');
      await axios.post(`${API_URL}/facturespro/manual`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      showAddToast('✅ Facture enregistrée.', 'success');
      setShowAddModal(false);
      setAddForm(EMPTY_ADD_FORM);
      setAddPreview(null);
      setAddOcrText('');
      fetchFromDB();
    } catch (e) {
      showAddToast('Erreur: ' + (e.response?.data?.message || e.message), 'danger');
    }
    setAddSaving(false);
  };

  // ── Synchronisation Peppyrus ───────────────────────────────────────────────
  const syncPeppyrus = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await axios.get(`${API_URL}/facturespro/sync`, config);
      setSyncResult(res.data.synced);
      setFactures(res.data.data || []);
      // Rafraîchir les stats
      const statsRes = await axios.get(`${API_URL}/facturespro/stats`, config);
      setStats(statsRes.data.data || null);
    } catch (err) {
      alert('Erreur synchronisation Peppyrus : ' + (err.response?.data?.message || err.message));
    } finally {
      setSyncing(false);
    }
  };

  // ── Purger les messages mal importés (MLR/IR/sortants MKW) ─────────────────
  // Vide les faux enregistrements → le prochain sync peut importer les vraies factures
  const purgerMessages = async () => {
    if (!window.confirm(
      'Purger les messages Peppyrus incorrectement importés ?\n\n' +
      'Cette action supprime :\n' +
      '  • Les factures envoyées par MKW (non des achats)\n' +
      '  • Les accusés de réception (MLR/Invoice Response)\n\n' +
      'Le prochain sync pourra ensuite importer vos vraies factures fournisseurs.'
    )) return;
    try {
      const res = await axios.delete(`${API_URL}/facturespro/purge-own`, config);
      const nb  = res.data.deleted ?? '?';
      alert('✅ ' + nb + ' entrée(s) purgée(s). Cliquez sur "Synchroniser Peppyrus" pour importer vos factures.');
      // Rafraîchir
      const listRes = await axios.get(`${API_URL}/facturespro`, config);
      setFactures(listRes.data.data || []);
      const statsRes = await axios.get(`${API_URL}/facturespro/stats`, config);
      setStats(statsRes.data.data || null);
    } catch (err) {
      alert('Erreur purge : ' + (err.response?.data?.message || err.message));
    }
  };

  // ── Charger l'inbox Peppyrus (messages disponibles avec leurs UUIDs API) ─
  const chargerInboxPeppyrus = async () => {
    setInboxLoading(true);
    try {
      const res = await axios.get(`${API_URL}/facturespro/peppyrus-inbox`, config);
      setInboxPeppyrus(res.data.data || []);
    } catch (err) {
      setInboxPeppyrus([]);
      setImportResult({ success: false, message: 'Impossible de charger l\'inbox Peppyrus : ' + (err.response?.data?.message || err.message) });
    } finally {
      setInboxLoading(false);
    }
  };

  // ── Import manuel par UUID Peppyrus ──────────────────────────────────────
  const importerParIdDirecte = async (id) => {
    if (!id) return;
    setImportLoading(true);
    setImportResult(null);
    try {
      const res = await axios.post(`${API_URL}/facturespro/import-by-id`, { peppyrus_id: id }, config);
      setImportResult({ success: true, message: res.data.message });
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/facturespro`, config),
        axios.get(`${API_URL}/facturespro/stats`, config),
      ]);
      setFactures(listRes.data.data || []);
      setStats(statsRes.data.data || null);
      // Rafraîchir la liste inbox (pour marquer le message comme "déjà importé")
      if (inboxPeppyrus !== null) chargerInboxPeppyrus();
    } catch (err) {
      setImportResult({ success: false, message: err.response?.data?.message || err.message });
    } finally {
      setImportLoading(false);
    }
  };

  const importerParId = () => importerParIdDirecte(importId.trim());

  // ── Ouvrir modal détail ───────────────────────────────────────────────────
  const openDetail = (fa) => {
    setSelected(fa);
    setMotifRejet('');
    setShowModal(true);
    setShowXML(false);
    setShowPDF(false);
    setEditMode(false);
    setEditToast(null);
    if (pdfUrl) { window.URL.revokeObjectURL(pdfUrl); setPdfUrl(null); }
  };

  // Helper : lire un Blob comme texte et détecter si c'est du JSON d'erreur
  const readBlobError = (blob) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const json = JSON.parse(reader.result);
        resolve(json?.message || 'Erreur serveur inconnue');
      } catch {
        resolve(null); // pas du JSON = vrai PDF
      }
    };
    reader.onerror = () => resolve(null);
    reader.readAsText(blob);
  });

  // ── Voir PDF inline (iframe dans la modal) ───────────────────────────────
  const viewPDF = async (id) => {
    if (showPDF) { setShowPDF(false); return; }
    try {
      const res = await axios.get(`${API_URL}/facturespro/${id}/pdf`, {
        ...config, responseType: 'blob'
      });
      // Détecter si la réponse est un JSON d'erreur déguisé en blob
      const errMsg = await readBlobError(res.data);
      if (errMsg) {
        alert('Erreur génération PDF : ' + errMsg);
        return;
      }
      if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPdfUrl(url);
      setShowPDF(true);
      setShowXML(false);
    } catch (err) {
      // Si le serveur retourne 4xx/5xx, axios throw avec response.data = Blob
      if (err.response?.data instanceof Blob) {
        const errMsg = await readBlobError(err.response.data);
        alert('Erreur PDF : ' + (errMsg || err.message));
      } else {
        alert('Erreur génération PDF : ' + (err.response?.data?.message || err.message));
      }
    }
  };

  // ── Télécharger PDF ───────────────────────────────────────────────────────
  const downloadPDF = async (id, fournisseur, numero) => {
    try {
      const res = await axios.get(`${API_URL}/facturespro/${id}/pdf`, {
        ...config, responseType: 'blob'
      });
      // Détecter si la réponse est un JSON d'erreur
      const errMsg = await readBlobError(res.data);
      if (errMsg) {
        alert('Erreur génération PDF : ' + errMsg);
        return;
      }
      const url  = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', `FACTURE_PEPPOL_${(fournisseur || '').replace(/\s/g,'_')}_${numero || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.data instanceof Blob) {
        const errMsg = await readBlobError(err.response.data);
        alert('Erreur téléchargement PDF : ' + (errMsg || err.message));
      } else {
        alert('Erreur téléchargement PDF : ' + (err.response?.data?.message || err.message));
      }
    }
  };

  // ── Télécharger XML ───────────────────────────────────────────────────────
  const downloadXML = async (id, fournisseur) => {
    try {
      const res = await axios.get(`${API_URL}/facturespro/${id}/xml`, {
        ...config, responseType: 'blob',
      });
      const url  = window.URL.createObjectURL(new Blob([res.data], { type: 'application/xml' }));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', `RECU_${fournisseur.replace(/\s/g, '_')}_${id}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('XML non disponible : ' + (err.response?.data?.message || err.message));
    }
  };

  // ── Afficher XML inline ───────────────────────────────────────────────────
  const showXMLInline = (fa) => {
    if (!fa.xml_brut) return alert('XML non disponible pour cette facture.');
    setXmlContent(fa.xml_brut);
    setShowXML(true);
  };

  // ── Changer statut ────────────────────────────────────────────────────────
  const changerStatut = async (id, statut, motif = '') => {
    setActionLoading(true);
    try {
      await axios.patch(`${API_URL}/facturespro/${id}/statut`, {
        statut,
        motif_rejet: motif || undefined,
      }, config);
      setShowModal(false); setEditMode(false);
      await fetchFromDB();
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  // ── Supprimer ─────────────────────────────────────────────────────────────
  const supprimer = async (id) => {
    if (!window.confirm('Supprimer cette facture d\'achat ? Cette action est irréversible.')) return;
    try {
      await axios.delete(`${API_URL}/facturespro/${id}`, config);
      setShowModal(false); setEditMode(false);
      await fetchFromDB();
    } catch (err) {
      alert('Erreur suppression : ' + (err.response?.data?.message || err.message));
    }
  };

  // ── Filtres rapides ───────────────────────────────────────────────────────
  const filtres = [
    { key: 'tous',               label: 'Toutes',        count: stats?.total || '—'         },
    { key: 'recue',              label: '📬 Reçues',     count: stats?.recues || '0'        },
    { key: 'validee',            label: '✅ Validées',   count: stats?.validees || '0'      },
    { key: 'en_attente_paiement',label: '⏳ À payer',    count: ''                          },
    { key: 'en_retard',          label: '🔴 En retard',  count: stats?.en_retard || '0'     },
    { key: 'payee',              label: '💚 Payées',     count: stats?.payees || '0'        },
    { key: 'rejetee',            label: '❌ Rejetées',   count: stats?.rejetees || '0'      },
  ];

  // ── Filtre période (frontend-only, ne déclenche pas de nouvel appel API) ──
  const MOIS_FR_PRO = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
  const anneesDisposPro = (() => {
    const ys = [...new Set(
      factures.map(fa => {
        const d = new Date(fa.date_emission || fa.date_reception);
        return isNaN(d) ? null : d.getFullYear();
      }).filter(y => y !== null && y > 2000)
    )].sort((a, b) => b - a);
    if (!ys.includes(new Date().getFullYear())) ys.unshift(new Date().getFullYear());
    return ys;
  })();
  const matchDatePro = (fa) => {
    if (periodeType === 'tout') return true;
    const ref = fa.date_emission || fa.date_reception;
    if (!ref) return false;
    const d = new Date(ref);
    if (isNaN(d)) return false;
    if (d.getFullYear() !== periodeAnnee) return false;
    if (periodeType === 'mois')      return d.getMonth() === periodeMois;
    if (periodeType === 'trimestre') return Math.floor(d.getMonth() / 3) === periodeQ;
    return true;
  };
  const facturesFiltrees  = factures.filter(matchDatePro);
  const totalHTFiltrees   = facturesFiltrees.reduce((s, fa) => s + (fa.type_document === 'credit_note' ? -1 : 1) * parseFloat(fa.montant_ht  || 0), 0);
  const totalTTCFiltrees  = facturesFiltrees.reduce((s, fa) => s + (fa.type_document === 'credit_note' ? -1 : 1) * parseFloat(fa.montant_ttc || 0), 0);
  const fmtEurPro = v => v.toLocaleString('fr-BE', { style: 'currency', currency: 'EUR' });

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: theme.pageBg }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)', padding: '20px 24px', color: 'white' }}>
        <div style={{ maxWidth: '1300px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '700' }}>
              🌐 FacturesPro — Réception Peppol
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '13px', opacity: 0.85 }}>
              Factures d'achat reçues via l'Access Point Peppyrus &mdash; Réseau européen Peppol
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            {syncResult && (
              <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '8px', padding: '8px 14px', fontSize: '13px' }}>
                {syncResult.nouvelles > 0
                  ? `✅ ${syncResult.nouvelles} nouvelle${syncResult.nouvelles > 1 ? 's' : ''} facture${syncResult.nouvelles > 1 ? 's' : ''}`
                  : '✓ Aucune nouvelle facture'}
                {syncResult.doublons > 0 && ` · ${syncResult.doublons} doublons ignorés`}
                {syncResult.erreurs  > 0 && ` · ⚠️ ${syncResult.erreurs} erreur${syncResult.erreurs > 1 ? 's' : ''} — utilisez Import manuel`}
              </div>
            )}
            <button
              onClick={() => { setShowAddModal(true); setAddForm(EMPTY_ADD_FORM); setAddPreview(null); setAddOcrText(''); setAddMode('manuel'); }}
              style={{ ...S.btn, fontSize: '13px', padding: '10px 18px',
                background: 'rgba(255,255,255,0.25)', color: 'white',
                border: '1px solid rgba(255,255,255,0.5)', cursor: 'pointer',
                fontWeight: '700' }}
              title="Ajouter une facture d'achat (saisie manuelle ou upload PDF + OCR)"
            >
              ➕ Ajouter
            </button>
            <button
              onClick={syncPeppyrus}
              disabled={syncing}
              style={{
                ...S.btn, fontSize: '13px', padding: '10px 18px',
                background: syncing ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)',
                color: 'white', border: '1px solid rgba(255,255,255,0.4)',
                cursor: syncing ? 'wait' : 'pointer',
              }}
            >
              {syncing ? '⏳ Synchronisation…' : '🔄 Synchroniser Peppyrus'}
            </button>
            <button
              onClick={() => { setShowImportModal(true); setImportId(''); setImportResult(null); }}
              title="Importer manuellement une facture par son UUID Peppyrus (factures volumineuses non importées par le sync automatique)"
              style={{ ...S.btn, fontSize: '13px', padding: '10px 18px',
                background: 'rgba(255,255,255,0.2)', color: 'white',
                border: '1px solid rgba(255,255,255,0.4)', cursor: 'pointer' }}
            >
              📥 Import manuel
            </button>
            <button
              onClick={purgerMessages}
              title="Supprimer les messages mal importés (MLR, IR, propres envois) pour permettre le ré-import des vraies factures"
              style={{ ...S.btn, fontSize: '13px', padding: '10px 18px',
                background: 'rgba(220,38,38,0.3)', color: 'white',
                border: '1px solid rgba(255,150,150,0.5)', cursor: 'pointer' }}
            >
              🗑️ Purger messages
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1300px', margin: '0 auto', padding: '24px' }}>

        {/* ── Statistiques ─────────────────────────────────────────────────── */}
        {stats && (
          <div style={{
            display: 'flex',
            gap: '10px',
            marginBottom: '20px',
            overflowX: isMobile ? 'auto' : 'visible',
            flexWrap: isMobile ? 'nowrap' : 'wrap',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
          }}>
            <StatCard icon="📬" label="Total reçues"    value={stats.total}                      color={theme.primary}
              wrapStyle={{ flex: isMobile ? '0 0 calc(50% - 5px)' : '1 1 200px' }} />
            <StatCard icon="⏳" label="En retard"       value={stats.en_retard || 0}             color={theme.danger}
              sub={stats.en_retard > 0 ? 'Échéance dépassée' : 'Tout à jour'}
              wrapStyle={{ flex: isMobile ? '0 0 calc(50% - 5px)' : '1 1 200px' }} />
            <StatCard icon="💶" label="Encours à payer" value={`${eur(stats.total_restant)} €`}  color={theme.warning || '#f39c12'}
              wrapStyle={{ flex: isMobile ? '0 0 calc(50% - 5px)' : '1 1 200px' }} />
            <StatCard icon="✅" label="Total payé"      value={`${eur(stats.total_paye)} €`}     color={theme.success}
              wrapStyle={{ flex: isMobile ? '0 0 calc(50% - 5px)' : '1 1 200px' }} />
          </div>
        )}

        {/* ── Filtres + Recherche + Période ─────────────────────────────── */}
        <div style={{ ...S.card, marginBottom: '10px', padding: isMobile ? '10px 12px' : '16px 20px' }}>
          {/* Ligne 1 : filtres statut — scroll horizontal sur mobile */}
          <div style={{
            display: 'flex', gap: '6px', marginBottom: '10px',
            overflowX: isMobile ? 'auto' : 'visible',
            flexWrap: isMobile ? 'nowrap' : 'wrap',
            scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch',
          }}>
            {filtres.map(f => (
              <button key={f.key} onClick={() => setFiltreStatut(f.key)} style={{
                ...S.btn,
                background: filtreStatut === f.key ? '#1a237e' : theme.primaryBg,
                color:      filtreStatut === f.key ? 'white'   : theme.textPrimary,
                padding: isMobile ? '6px 10px' : '7px 14px',
                fontSize: isMobile ? '11px' : '12px',
                whiteSpace: 'nowrap',
              }}>
                {f.label}{f.count !== '' && <span style={{ background: 'rgba(255,255,255,0.3)', borderRadius: '10px', padding: '0 6px', fontSize: '11px', marginLeft: '4px' }}>{f.count}</span>}
              </button>
            ))}
          </div>

          {/* Ligne 2 : filtre période */}
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '11px', color: '#999', fontWeight: '600', whiteSpace: 'nowrap' }}>Période :</span>
            {[{k:'tout',l:'Tout'},{k:'mois',l:'Mois'},{k:'trimestre',l:'Trimestre'}].map(({k,l}) => (
              <button key={k} onClick={() => setPeriodeType(k)} style={{
                padding: isMobile ? '5px 10px' : '6px 13px', borderRadius: '20px', border: 'none',
                cursor: 'pointer', fontSize: isMobile ? '11px' : '12px', fontWeight: '600',
                background: periodeType === k ? '#7c3aed' : '#f0f2f5',
                color:      periodeType === k ? 'white'   : '#666',
                transition: 'all 0.15s', whiteSpace: 'nowrap',
              }}>{l}</button>
            ))}
            {periodeType !== 'tout' && (
              <select value={periodeAnnee} onChange={e => setPeriodeAnnee(parseInt(e.target.value))} style={{
                padding: isMobile ? '5px 7px' : '6px 10px', borderRadius: '8px',
                border: '1.5px solid #7c3aed', fontSize: isMobile ? '12px' : '13px',
                background: '#faf5ff', color: '#7c3aed', fontWeight: '700',
                cursor: 'pointer', outline: 'none',
              }}>
                {anneesDisposPro.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
            {periodeType === 'mois' && (
              <select value={periodeMois} onChange={e => setPeriodeMois(parseInt(e.target.value))} style={{
                padding: isMobile ? '5px 7px' : '6px 10px', borderRadius: '8px',
                border: '1.5px solid #7c3aed', fontSize: isMobile ? '12px' : '13px',
                background: '#faf5ff', color: '#5b21b6', fontWeight: '600',
                cursor: 'pointer', outline: 'none',
              }}>
                {MOIS_FR_PRO.map((m, i) => <option key={i} value={i}>{m}</option>)}
              </select>
            )}
            {periodeType === 'trimestre' && (
              <>
                {[{q:0,l:'Q1'},{q:1,l:'Q2'},{q:2,l:'Q3'},{q:3,l:'Q4'}].map(({q,l}) => (
                  <button key={q} onClick={() => setPeriodeQ(q)} style={{
                    padding: isMobile ? '5px 10px' : '6px 13px', borderRadius: '8px', border: 'none',
                    cursor: 'pointer', fontSize: isMobile ? '12px' : '13px', fontWeight: '700',
                    background: periodeQ === q ? '#7c3aed' : '#ede9fe',
                    color:      periodeQ === q ? 'white'   : '#5b21b6',
                    transition: 'all 0.15s', minWidth: isMobile ? '38px' : '44px',
                  }}>{l}</button>
                ))}
              </>
            )}
          </div>

          {/* Ligne 3 : recherche */}
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="🔍 Fournisseur, N° facture, TVA…"
            style={{
              width: '100%', padding: '8px 14px', border: `1px solid ${theme.border}`,
              borderRadius: '8px', fontSize: '13px',
              outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>

        {/* ── Bande récapitulative HT / TTC ─────────────────────────────── */}
        {facturesFiltrees.length > 0 && (
          <div style={{
            display: 'flex', gap: isMobile ? '8px' : '16px',
            flexWrap: 'wrap', alignItems: 'center',
            justifyContent: isMobile ? 'space-between' : 'flex-end',
            background: 'white', borderRadius: '10px',
            padding: isMobile ? '9px 12px' : '8px 18px',
            marginBottom: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
            fontSize: isMobile ? '12px' : '13px',
          }}>
            <span style={{ color: '#888', fontWeight: 500 }}>
              {facturesFiltrees.length} facture{facturesFiltrees.length > 1 ? 's' : ''}
            </span>
            <span style={{ fontWeight: 600, color: '#555' }}>
              HT : <span style={{ color: '#1a237e' }}>{fmtEurPro(totalHTFiltrees)}</span>
            </span>
            <span style={{ fontWeight: 700, color: '#333' }}>
              TTC : <span style={{ color: '#1a237e' }}>{fmtEurPro(totalTTCFiltrees)}</span>
            </span>
          </div>
        )}

        {/* ── Tableau factures reçues ────────────────────────────────────── */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: theme.textMuted, fontSize: '16px' }}>
            ⏳ Chargement…
          </div>
        ) : factures.length === 0 ? (
          <div style={{ ...S.card, textAlign: 'center', padding: '60px' }}>
            <p style={{ fontSize: '48px', margin: '0 0 16px' }}>📭</p>
            <p style={{ fontSize: '16px', color: theme.textMuted }}>Aucune facture reçue.</p>
            <p style={{ fontSize: '14px', color: theme.textMuted, marginTop: '8px' }}>
              Cliquez sur <strong>Synchroniser Peppyrus</strong> pour récupérer les factures depuis votre boîte Peppol.
            </p>
          </div>
        ) : facturesFiltrees.length === 0 ? (
          <div style={{ ...S.card, textAlign: 'center', padding: '60px' }}>
            <p style={{ fontSize: '48px', margin: '0 0 16px' }}>🔍</p>
            <p style={{ fontSize: '16px', color: theme.textMuted }}>Aucune facture pour cette période.</p>
          </div>
        ) : (
          <div style={S.card}>
            {/* Mobile: cards par facture d'achat */}
            {isMobile && (
              <div style={{ display:'flex', flexDirection:'column', gap:'8px', padding:'10px' }}>
                {facturesFiltrees.map(fa => {
                  const enRetard = fa.date_echeance && new Date(fa.date_echeance) < new Date()
                    && !['payee','rejetee'].includes(fa.statut);
                  const isCreditNote = fa.type_document === 'credit_note';
                  return (
                    <div key={fa.id_facture_achat}
                      onClick={() => openDetail(fa)}
                      style={{ background: enRetard ? '#fff8f8' : 'white', borderRadius:'12px',
                        padding:'12px 14px', boxShadow:'0 1px 5px rgba(0,0,0,0.08)',
                        borderLeft:'3px solid ' + (enRetard ? '#dc2626' : '#1a237e'), cursor:'pointer' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'6px' }}>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontWeight:'800', fontSize:'14px', color:'#1a237e', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {fa.nom_fournisseur||'—'}
                          </div>
                          {fa.numero_tva_fournisseur && (
                            <div style={{ fontSize:'11px', color:'#1565c0', marginTop:'1px' }}>🏢 {fa.numero_tva_fournisseur}</div>
                          )}
                        </div>
                        <div style={{ textAlign:'right', flexShrink:0, marginLeft:'10px' }}>
                          <div style={{ fontWeight:'800', fontSize:'15px', color: isCreditNote ? '#dc2626' : '#1a237e' }}>
                            {isCreditNote ? '-' : ''}{eur(fa.montant_ttc)} €
                          </div>
                          <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:'3px' }}>
                            <StatutBadge statut={fa.statut} />
                            {isCreditNote && (
                              <span style={{ fontSize:'10px', fontWeight:'700', background:'#fce4ec', color:'#c62828', borderRadius:'4px', padding:'1px 5px' }}>
                                🔄 Avoir
                              </span>
                            )}
                            {fa.deduction_majoree_120 && (
                              <span style={{ fontSize:'10px', fontWeight:'700', background:'#f3e5f5', color:'#6a1b9a', borderRadius:'4px', padding:'1px 5px' }}>
                                💼 120% IS
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:'11px', color:'#666', marginBottom:'8px' }}>
                        <span style={{ fontWeight:'600', color:'#1a237e' }}>{fa.numero_facture_fournisseur||'—'}</span>
                        <span>📅 {fmtDate(fa.date_emission)}
                          {fa.date_echeance && (
                            <span style={{ color: enRetard ? '#dc2626' : 'inherit' }}>
                              {' → '}{fmtDate(fa.date_echeance)}{enRetard && ' ⚠️'}
                            </span>
                          )}
                        </span>
                      </div>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <span style={{ fontSize:'11px', color:'#888' }}>HT: <strong>{eur(fa.montant_ht)} €</strong></span>
                        <div style={{ display:'flex', gap:'5px' }}>
                          <button onClick={e => { e.stopPropagation(); openDetail(fa); }}
                            style={{ ...S.btn, background:'#e8eaf6', color:'#1a237e', fontSize:'11px', padding:'4px 9px' }}>
                            🔍 Détail
                          </button>
                          {fa.xml_brut && (
                            <button onClick={e => { e.stopPropagation(); downloadXML(fa.id_facture_achat, fa.nom_fournisseur); }}
                              style={{ ...S.btn, background:'#e3f2fd', color:'#0d47a1', fontSize:'11px', padding:'4px 9px' }}>
                              📄 XML
                            </button>
                          )}
                        </div>
                      </div>
                      {fa.peppol_id_fournisseur && (
                        <div style={{ fontSize:'10px', color:'#999', marginTop:'4px' }}>Peppol: {fa.peppol_id_fournisseur}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {/* Desktop: table originale */}
            {!isMobile && (
              <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth:'680px' }}>
              <thead>
                <tr style={{ background: '#1a237e', color: 'white' }}>
                  <th style={{ padding: '12px 14px', textAlign: 'left',   fontSize: '12px' }}>Fournisseur</th>
                  <th style={{ padding: '12px 14px', textAlign: 'left',   fontSize: '12px' }}>N° Facture</th>
                  <th style={{ padding: '12px 14px', textAlign: 'left',   fontSize: '12px' }}>Émission</th>
                  <th style={{ padding: '12px 14px', textAlign: 'left',   fontSize: '12px' }}>Échéance</th>
                  <th style={{ padding: '12px 14px', textAlign: 'right',  fontSize: '12px' }}>HT</th>
                  <th style={{ padding: '12px 14px', textAlign: 'right',  fontSize: '12px' }}>TTC</th>
                  <th style={{ padding: '12px 14px', textAlign: 'center', fontSize: '12px' }}>Statut</th>
                  <th style={{ padding: '12px 14px', textAlign: 'center', fontSize: '12px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {facturesFiltrees.map(fa => {
                  const enRetard = fa.date_echeance
                    && new Date(fa.date_echeance) < new Date()
                    && !['payee', 'rejetee'].includes(fa.statut);
                  const isCreditNote = fa.type_document === 'credit_note';
                  const rowBg = enRetard ? '#fff8f8' : 'white';

                  return (
                    <tr key={fa.id_facture_achat}
                      style={{ borderBottom: `1px solid ${theme.border}`, background: rowBg, cursor: 'pointer' }}
                      onClick={() => openDetail(fa)}
                    >
                      <td style={{ padding: '12px 14px' }}>
                        <div style={{ fontWeight: '600', fontSize: '14px' }}>{fa.nom_fournisseur}</div>
                        {fa.numero_tva_fournisseur && (
                          <div style={{ fontSize: '11px', color: '#1565c0', marginTop: '2px' }}>
                            🏢 {fa.numero_tva_fournisseur}
                          </div>
                        )}
                        {fa.peppol_id_fournisseur && (
                          <div style={{ fontSize: '10px', color: theme.textMuted }}>
                            Peppol: {fa.peppol_id_fournisseur}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: '13px', color: theme.primary, fontWeight: '600' }}>
                        {fa.numero_facture_fournisseur || '—'}
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: '13px', color: theme.textSecondary }}>
                        {fmtDate(fa.date_emission)}
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: '13px' }}>
                        <span style={{ color: enRetard ? theme.danger : theme.textSecondary, fontWeight: enRetard ? '700' : '400' }}>
                          {fmtDate(fa.date_echeance)}
                          {enRetard && ' ⚠️'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'right', fontSize: '13px', color: theme.textSecondary }}>
                        {eur(fa.montant_ht)} €
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'right', fontSize: '14px', fontWeight: '700', color: isCreditNote ? '#dc2626' : theme.textPrimary }}>
                        {isCreditNote ? '-' : ''}{eur(fa.montant_ttc)} €
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                          <StatutBadge statut={fa.statut} />
                          {isCreditNote && (
                            <span style={{ fontSize: '10px', fontWeight: '700', background: '#fce4ec', color: '#c62828', borderRadius: '4px', padding: '1px 6px' }}>
                              🔄 Avoir
                            </span>
                          )}
                          {fa.deduction_majoree_120 && (
                            <span style={{ fontSize: '10px', fontWeight: '700', background: '#f3e5f5', color: '#6a1b9a', borderRadius: '4px', padding: '1px 6px' }}>
                              💼 120% IS
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                          <button
                            onClick={e => { e.stopPropagation(); openDetail(fa); }}
                            style={{ ...S.btn, background: theme.primaryBg, color: theme.primary }}
                            title="Voir le détail et gérer"
                          >🔍 Détail</button>
                          {fa.xml_brut && (
                            <button
                              onClick={e => { e.stopPropagation(); downloadXML(fa.id_facture_achat, fa.nom_fournisseur); }}
                              style={{ ...S.btn, background: '#e8eaf6', color: '#1a237e' }}
                              title="Télécharger le XML UBL"
                            >📄 XML</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
                </table>
              </div>
            )}

            {/* Pied de tableau */}
            <div style={{ padding: '12px 18px', borderTop: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', color: theme.textMuted }}>
              <span>{facturesFiltrees.length} facture{facturesFiltrees.length > 1 ? 's' : ''} affichée{facturesFiltrees.length > 1 ? 's' : ''}</span>
              <span>Total TTC : <strong style={{ color: '#1a237e' }}>{eur(totalTTCFiltrees)} €</strong></span>
            </div>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          MODAL DÉTAIL FACTURE
      ══════════════════════════════════════════════════════════════════════ */}
      {showModal && selected && (
        <div style={S.overlay} onClick={() => setShowModal(false)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>

            {/* Header modal */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', gap: '10px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 0', minWidth: 0 }}>
                <h2 style={{ margin: '0 0 6px', color: '#1a237e', fontSize: isMobile ? '15px' : '18px', wordBreak: 'break-word' }}>
                  📄 Facture reçue — {selected.nom_fournisseur}
                </h2>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <StatutBadge statut={selected.statut} />
                  {selected.type_document === 'credit_note' && (
                    <span style={{ fontSize: '11px', fontWeight: '700', background: '#fce4ec', color: '#c62828', borderRadius: '4px', padding: '2px 8px' }}>🔄 Avoir</span>
                  )}
                  {selected.origine && selected.origine !== 'peppol' && (
                    <span style={{ fontSize: '11px', fontWeight: '700', background: '#e3f2fd', color: '#1565c0', borderRadius: '4px', padding: '2px 8px' }}>
                      {selected.origine === 'ocr' ? '📷 OCR' : '✏️ Manuel'}
                    </span>
                  )}
                  {(() => {
                    const cat = CATEGORIES_TVA[selected.categorie_tva];
                    if (!cat || selected.categorie_tva === 'standard') return null;
                    return (
                      <span style={{ fontSize: '11px', fontWeight: '700', background: cat.bg, color: cat.color, borderRadius: '4px', padding: '2px 8px' }}>
                        {cat.icon} {cat.label} — {selected.tva_deductible_pct ?? cat.pct}% déductible
                      </span>
                    );
                  })()}
                  {selected.deduction_majoree_120 && (
                    <span style={{ fontSize: '11px', fontWeight: '700', background: '#f3e5f5', color: '#6a1b9a', borderRadius: '4px', padding: '2px 8px' }}>
                      💼 Déduction 120% IS/IPP
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {selected.origine !== 'peppol' && !editMode && (
                  <button onClick={startEdit}
                    style={{ ...S.btn, background: '#e8eaf6', color: '#1a237e', fontSize: '13px', padding: '7px 14px' }}>
                    ✏️ Modifier
                  </button>
                )}
                <button onClick={() => { setShowModal(false); setEditMode(false); }}
                  style={{ ...S.btn, background: theme.dangerBg, color: theme.danger, fontSize: '14px' }}>
                  ✕
                </button>
              </div>
            </div>

            {/* Toast édition */}
            {editToast && (
              <div style={{ marginBottom: '12px', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
                background: editToast.type === 'success' ? '#dcfce7' : editToast.type === 'warning' ? '#fff8e1' : '#fee2e2',
                color:      editToast.type === 'success' ? '#166534' : editToast.type === 'warning' ? '#92400e' : '#991b1b' }}>
                {editToast.msg}
              </div>
            )}

            {/* Informations fournisseur */}
            <div style={{ background: '#e8eaf6', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
              <div style={{ fontWeight: '700', color: '#1a237e', marginBottom: '10px', fontSize: '14px' }}>🏢 Fournisseur</div>
              {editMode ? (
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '8px' }}>
                  {[
                    { key: 'nom_fournisseur',            label: 'Nom *',              placeholder: 'Nom de la société' },
                    { key: 'numero_tva_fournisseur',     label: 'N° TVA',             placeholder: 'BE0123456789 / DE262330242' },
                    { key: 'email_fournisseur',          label: 'Email',              placeholder: 'contact@fournisseur.com' },
                    { key: 'iban_fournisseur',           label: 'IBAN',               placeholder: 'BE12 3456 7890 1234' },
                    { key: 'numero_facture_fournisseur', label: 'N° Facture',         placeholder: 'FACT-2026-001' },
                  ].map(({ key, label, placeholder }) => (
                    <div key={key}>
                      <div style={{ fontSize: '11px', color: '#666', marginBottom: '3px', fontWeight: '700', textTransform: 'uppercase' }}>{label}</div>
                      <input value={editForm[key]} onChange={e => setEditForm(p => ({ ...p, [key]: e.target.value }))}
                        placeholder={placeholder}
                        style={{ width: '100%', padding: '8px 10px', border: `1.5px solid ${key === 'nom_fournisseur' && !editForm[key]?.trim() ? '#fca5a5' : '#c5cae9'}`,
                          borderRadius: '7px', fontSize: '13px', boxSizing: 'border-box', outline: 'none', background: 'white' }} />
                    </div>
                  ))}
                  <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
                    <div style={{ fontSize: '11px', color: '#666', marginBottom: '3px', fontWeight: '700', textTransform: 'uppercase' }}>Adresse</div>
                    <input value={editForm.adresse_fournisseur} onChange={e => setEditForm(p => ({ ...p, adresse_fournisseur: e.target.value }))}
                      placeholder="Rue, code postal, ville, pays"
                      style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #c5cae9',
                        borderRadius: '7px', fontSize: '13px', boxSizing: 'border-box', outline: 'none', background: 'white' }} />
                  </div>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px' }}>
                  <div><span style={{ color: '#666' }}>Nom : </span><strong>{selected.nom_fournisseur}</strong></div>
                  <div><span style={{ color: '#666' }}>N° TVA : </span><strong style={{ color: '#1a237e' }}>{selected.numero_tva_fournisseur || '—'}</strong></div>
                  {selected.peppol_id_fournisseur && <div><span style={{ color: '#666' }}>Peppol ID : </span><code style={{ fontSize: '11px', background: '#f0f0f0', padding: '1px 5px', borderRadius: '3px' }}>{selected.peppol_id_fournisseur}</code></div>}
                  {selected.email_fournisseur && <div><span style={{ color: '#666' }}>Email : </span>{selected.email_fournisseur}</div>}
                  {selected.adresse_fournisseur && <div style={{ gridColumn: '1/-1' }}><span style={{ color: '#666' }}>Adresse : </span>{selected.adresse_fournisseur}</div>}
                </div>
              )}
            </div>

            {/* Informations facture */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div style={{ background: '#f5f7fb', borderRadius: '8px', padding: '14px' }}>
                <div style={{ color: '#666', fontSize: '12px', marginBottom: '4px' }}>N° FACTURE FOURNISSEUR</div>
                <div style={{ fontWeight: '700', fontSize: '15px', color: '#1a237e' }}>{selected.numero_facture_fournisseur || '—'}</div>
              </div>
              <div style={{ background: '#f5f7fb', borderRadius: '8px', padding: '14px' }}>
                <div style={{ color: '#666', fontSize: '12px', marginBottom: '4px' }}>REÇUE LE</div>
                <div style={{ fontWeight: '600', fontSize: '14px' }}>{fmtDate(selected.date_reception)}</div>
              </div>
              <div style={{ background: '#f5f7fb', borderRadius: '8px', padding: '14px' }}>
                <div style={{ color: '#666', fontSize: '12px', marginBottom: '4px' }}>DATE ÉMISSION</div>
                {editMode
                  ? <input type="date" value={editForm.date_emission} onChange={e => setEditForm(p => ({ ...p, date_emission: e.target.value }))}
                      style={{ width: '100%', padding: '6px 8px', border: '1.5px solid #c5cae9', borderRadius: '6px', fontSize: '13px', outline: 'none' }} />
                  : <div style={{ fontWeight: '600' }}>{fmtDate(selected.date_emission)}</div>
                }
              </div>
              <div style={{ background: '#f5f7fb', borderRadius: '8px', padding: '14px' }}>
                <div style={{ color: '#666', fontSize: '12px', marginBottom: '4px' }}>ÉCHÉANCE PAIEMENT</div>
                {editMode
                  ? <input type="date" value={editForm.date_echeance} onChange={e => setEditForm(p => ({ ...p, date_echeance: e.target.value }))}
                      style={{ width: '100%', padding: '6px 8px', border: '1.5px solid #c5cae9', borderRadius: '6px', fontSize: '13px', outline: 'none' }} />
                  : <div style={{ fontWeight: '700', color: new Date(selected.date_echeance) < new Date() && !['payee','rejetee'].includes(selected.statut) ? theme.danger : theme.textPrimary }}>
                      {fmtDate(selected.date_echeance)}
                    </div>
                }
              </div>
            </div>

            {/* Montants */}
            {editMode ? (
              <div style={{ background: '#e8eaf6', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                <div style={{ fontWeight: '700', color: '#1a237e', marginBottom: '10px', fontSize: '14px' }}>💶 Montants</div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr', gap: '10px' }}>
                  {[
                    { key: 'montant_ht',  label: 'Montant HT (€) *' },
                    { key: 'montant_tva', label: 'TVA (€)' },
                    { key: 'montant_ttc', label: 'Total TTC (€) *' },
                    { key: 'taux_tva',    label: 'Taux TVA (%)' },
                  ].map(({ key, label }) => (
                    <div key={key}>
                      <div style={{ fontSize: '11px', color: '#666', marginBottom: '3px', fontWeight: '700', textTransform: 'uppercase' }}>{label}</div>
                      <input type="number" step="0.01" value={editForm[key]}
                        onChange={e => setEditForm(p => ({ ...p, [key]: e.target.value }))}
                        style={{ width: '100%', padding: '8px 10px', border: `1.5px solid ${['montant_ht','montant_ttc'].includes(key) && !editForm[key] ? '#fca5a5' : '#c5cae9'}`,
                          borderRadius: '7px', fontSize: '14px', fontWeight: '700', boxSizing: 'border-box', outline: 'none', background: 'white' }} />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ background: '#1a237e', borderRadius: '10px', padding: '16px', marginBottom: '16px', display: 'flex', justifyContent: 'space-around', color: 'white', textAlign: 'center' }}>
                <div>
                  <div style={{ fontSize: '12px', opacity: 0.8 }}>Montant HT</div>
                  <div style={{ fontSize: '18px', fontWeight: '700' }}>{eur(selected.montant_ht)} €</div>
                </div>
                <div style={{ borderLeft: '1px solid rgba(255,255,255,0.3)', padding: '0 24px' }}>
                  <div style={{ fontSize: '12px', opacity: 0.8 }}>TVA</div>
                  <div style={{ fontSize: '18px', fontWeight: '700' }}>{eur(selected.montant_tva)} €</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', opacity: 0.8 }}>MONTANT TTC</div>
                  <div style={{ fontSize: '24px', fontWeight: '900' }}>{eur(selected.montant_ttc)} €</div>
                </div>
              </div>
            )}

            {/* IBAN fournisseur si disponible — masqué en édition (l'IBAN est dans le formulaire) */}
            {!editMode && selected.iban_fournisseur && (
              <div style={{ background: theme.successBg, borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px' }}>
                <strong>💳 Coordonnées de paiement fournisseur :</strong><br />
                IBAN : <code style={{ background: '#f0f0f0', padding: '2px 8px', borderRadius: '4px' }}>{selected.iban_fournisseur}</code>
                &nbsp; Communication : <strong>{selected.numero_facture_fournisseur || selected.id_facture_achat}</strong>
              </div>
            )}

            {/* Notes admin — input en mode édition */}
            {editMode && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '11px', color: '#666', marginBottom: '3px', fontWeight: '700', textTransform: 'uppercase' }}>Notes internes</div>
                <textarea value={editForm.notes_admin} onChange={e => setEditForm(p => ({ ...p, notes_admin: e.target.value }))}
                  placeholder="Notes internes (non visibles sur la facture)"
                  rows={2}
                  style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #c5cae9', borderRadius: '7px',
                    fontSize: '13px', boxSizing: 'border-box', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
            )}

            {/* ── Catégorie TVA belge ─────────────────────────────────────────── */}
            <div style={{ background: '#f8f9fa', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px' }}>
              <div style={{ fontWeight: '700', fontSize: '13px', color: '#1a237e', marginBottom: '10px' }}>
                🏛️ Catégorie TVA (législation belge)
              </div>
              {isMobile ? (
                <select
                  value={selected.categorie_tva || 'standard'}
                  onChange={async e => {
                    const key = e.target.value;
                    const cat = CATEGORIES_TVA[key] || CATEGORIES_TVA.standard;
                    try {
                      await axios.patch(`${API_URL}/facturespro/${selected.id_facture_achat}/categorie-tva`,
                        { categorie_tva: key, tva_deductible_pct: cat.pct }, config);
                      setSelected(s => ({ ...s, categorie_tva: key, tva_deductible_pct: cat.pct }));
                      fetchFromDB();
                    } catch (_) {}
                  }}
                  style={{
                    width: '100%', padding: '9px 12px', marginBottom: '10px',
                    border: `1.5px solid ${(CATEGORIES_TVA[selected.categorie_tva] || CATEGORIES_TVA.standard).color}`,
                    borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box',
                    background: 'white', fontWeight: '600', outline: 'none',
                    color: (CATEGORIES_TVA[selected.categorie_tva] || CATEGORIES_TVA.standard).color,
                  }}
                >
                  {Object.entries(CATEGORIES_TVA).map(([key, cat]) => (
                    <option key={key} value={key}>{cat.icon} {cat.label} — {cat.loi}</option>
                  ))}
                </select>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                  {Object.entries(CATEGORIES_TVA).map(([key, cat]) => (
                    <button key={key}
                      onClick={async () => {
                        try {
                          await axios.patch(`${API_URL}/facturespro/${selected.id_facture_achat}/categorie-tva`,
                            { categorie_tva: key, tva_deductible_pct: cat.pct }, config);
                          setSelected(s => ({ ...s, categorie_tva: key, tva_deductible_pct: cat.pct }));
                          fetchFromDB();
                        } catch (_) {}
                      }}
                      style={{
                        padding: '4px 10px', borderRadius: '20px', border: '1.5px solid',
                        borderColor: selected.categorie_tva === key ? cat.color : '#ddd',
                        background:  selected.categorie_tva === key ? cat.bg : 'white',
                        color:       selected.categorie_tva === key ? cat.color : '#555',
                        fontSize: '11px', fontWeight: '600', cursor: 'pointer',
                      }}
                      title={cat.loi}
                    >
                      {cat.icon} {cat.label}
                    </button>
                  ))}
                </div>
              )}
              {(() => {
                const cat = CATEGORIES_TVA[selected.categorie_tva] || CATEGORIES_TVA.standard;
                return (
                  <div style={{ fontSize: '12px', color: cat.color, fontWeight: '600' }}>
                    → TVA déductible : <strong>{selected.tva_deductible_pct ?? cat.pct}%</strong>
                    <span style={{ fontWeight: '400', color: '#888', marginLeft: '8px' }}>{cat.loi}</span>
                  </div>
                );
              })()}
            </div>

            {/* ── Fiscal IS/IPP — Déduction majorée 120% ─────────────────── */}
            {isMobile ? (
              /* Mobile : inline layout — pas de flex pour éviter tout débordement horizontal */
              <div style={{ marginBottom: '14px', paddingTop: '6px', paddingBottom: '2px' }}>
                <label style={{ display: 'block', cursor: 'pointer' }}>
                  <input type="checkbox"
                    checked={!!selected.deduction_majoree_120}
                    onChange={async e => {
                      const newVal = e.target.checked;
                      setSelected(s => ({ ...s, deduction_majoree_120: newVal }));
                      try {
                        await axios.patch(
                          `${API_URL}/facturespro/${selected.id_facture_achat}/categorie-tva`,
                          { categorie_tva: selected.categorie_tva, tva_deductible_pct: selected.tva_deductible_pct, deduction_majoree_120: newVal },
                          config
                        );
                        fetchFromDB();
                      } catch { setSelected(s => ({ ...s, deduction_majoree_120: !newVal })); }
                    }}
                    style={{ accentColor: '#6a1b9a', width: '16px', height: '16px', verticalAlign: 'middle', cursor: 'pointer', marginRight: '8px' }} />
                  <span style={{ fontSize: '13px', color: '#6a1b9a', fontWeight: '600', verticalAlign: 'middle' }}>
                    💼 Déduction 120% IS/IPP
                  </span>
                </label>
                <div style={{ fontSize: '11px', color: '#888', marginTop: '4px', paddingLeft: '24px' }}>
                  Art. 64ter CIR92 — ≠ TVA
                </div>
              </div>
            ) : (
              /* Desktop : bloc explicatif complet */
              <div style={{ background: '#f3e5f5', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px', border: '1px solid #ce93d8' }}>
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ fontWeight: '700', fontSize: '13px', color: '#6a1b9a' }}>💼 Impôt des Sociétés / IPP</div>
                  <div style={{ fontWeight: '400', fontSize: '11px', color: '#888', marginTop: '2px' }}>Distinct de la TVA — information pour votre comptable</div>
                </div>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                  <input type="checkbox"
                    checked={!!selected.deduction_majoree_120}
                    onChange={async e => {
                      const newVal = e.target.checked;
                      setSelected(s => ({ ...s, deduction_majoree_120: newVal }));
                      try {
                        await axios.patch(
                          `${API_URL}/facturespro/${selected.id_facture_achat}/categorie-tva`,
                          { categorie_tva: selected.categorie_tva, tva_deductible_pct: selected.tva_deductible_pct, deduction_majoree_120: newVal },
                          config
                        );
                        fetchFromDB();
                      } catch { setSelected(s => ({ ...s, deduction_majoree_120: !newVal })); }
                    }}
                    style={{ marginTop: '2px', accentColor: '#6a1b9a', width: '16px', height: '16px', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: '600', fontSize: '13px', color: '#6a1b9a' }}>Déduction majorée 120% (Art. 64ter CIR92)</div>
                    <div style={{ fontSize: '11px', color: '#888', marginTop: '2px', lineHeight: 1.5 }}>
                      Cybersécurité · IA/Digital — base imposable IS/IPP à 120% du coût HT.{' '}
                      <strong style={{ color: '#6a1b9a' }}>≠ TVA</strong> (TVA déductible inchangée)
                    </div>
                  </div>
                </label>
              </div>
            )}

            {/* Notes admin — affichage lecture seule uniquement */}
            {!editMode && selected.notes_admin && (
              <div style={{ background: '#fff8e1', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px' }}>
                <strong>📝 Notes :</strong> {selected.notes_admin}
              </div>
            )}

            {/* Motif rejet si rejeté */}
            {selected.statut === 'rejetee' && selected.motif_rejet && (
              <div style={{ background: theme.dangerBg, borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: theme.danger }}>
                <strong>❌ Motif de rejet :</strong> {selected.motif_rejet}
              </div>
            )}

            {/* Parse error si présent */}
            {selected.parse_error && (
              <div style={{ background: '#fff3e0', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '12px', color: '#e65100' }}>
                ⚠️ Parsing XML incomplet : {selected.parse_error}. Les données peuvent être partielles.
              </div>
            )}

            {/* ── APERÇU PDF INLINE ─────────────────────────────────────────────── */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: showPDF ? '12px' : '0' }}>
                <button
                  onClick={() => viewPDF(selected.id_facture_achat)}
                  style={{
                    ...S.btn,
                    background: showPDF ? '#c62828' : '#1a237e',
                    color: 'white', fontSize: '13px', padding: '8px 18px',
                    fontWeight: '600',
                  }}>
                  {showPDF ? '🔼 Fermer l\'aperçu' : '🔍 Aperçu PDF'}
                </button>
                <button
                  onClick={() => downloadPDF(selected.id_facture_achat, selected.nom_fournisseur, selected.numero_facture_fournisseur)}
                  style={{ ...S.btn, background: '#e53935', color: 'white', fontSize: '13px', padding: '8px 18px', fontWeight: '600' }}>
                  ⬇️ Télécharger PDF
                </button>
              </div>
              {showPDF && pdfUrl && (
                <iframe
                  src={pdfUrl}
                  title="Aperçu PDF facture fournisseur"
                  style={{
                    width: '100%', height: '520px', border: 'none',
                    borderRadius: '8px', boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                  }}
                />
              )}
            </div>

            {/* Bouton afficher/masquer XML */}
            {selected.xml_brut && (
              <div style={{ marginBottom: '16px' }}>
                <button onClick={() => { setShowXML(!showXML); if (!showXML) setShowPDF(false); }}
                  style={{ ...S.btn, background: '#e8eaf6', color: '#1a237e', fontSize: '13px', padding: '8px 16px' }}>
                  {showXML ? '🔼 Masquer' : '🔽 Voir'} le XML UBL brut
                </button>
                {showXML && (
                  <pre style={{
                    marginTop: '10px', background: '#1a1a2e', color: '#a8d8a8',
                    borderRadius: '8px', padding: '14px', fontSize: '10px',
                    maxHeight: '250px', overflowY: 'auto', whiteSpace: 'pre-wrap',
                  }}>
                    {selected.xml_brut}
                  </pre>
                )}
              </div>
            )}

            {/* ── Boutons Sauvegarder / Annuler (mode édition) ── */}
            {editMode && (
              <div style={{ borderTop: `1px solid ${theme.border}`, paddingTop: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '4px' }}>
                <button onClick={saveEdit} disabled={editSaving}
                  style={{ ...S.btn, background: '#1a237e', color: 'white', fontSize: '14px', padding: '10px 22px', fontWeight: '700', opacity: editSaving ? 0.7 : 1 }}>
                  {editSaving ? '⏳ Sauvegarde…' : '💾 Sauvegarder les modifications'}
                </button>
                <button onClick={cancelEdit} disabled={editSaving}
                  style={{ ...S.btn, background: '#e8eaf6', color: '#1a237e', fontSize: '13px', padding: '10px 18px' }}>
                  Annuler
                </button>
              </div>
            )}

            {/* ── ACTIONS ── */}
            {!editMode && (
            <div style={{ borderTop: `1px solid ${theme.border}`, paddingTop: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>

              {/* Valider */}
              {['recue'].includes(selected.statut) && (
                <button onClick={() => changerStatut(selected.id_facture_achat, 'validee')}
                  disabled={actionLoading}
                  style={{ ...S.btn, background: theme.successBg, color: theme.success, fontSize: '13px', padding: '10px 18px' }}>
                  ✅ Valider la facture
                </button>
              )}

              {/* Marquer à payer */}
              {['validee'].includes(selected.statut) && (
                <button onClick={() => changerStatut(selected.id_facture_achat, 'en_attente_paiement')}
                  disabled={actionLoading}
                  style={{ ...S.btn, background: '#fff8e1', color: '#f57f17', fontSize: '13px', padding: '10px 18px' }}>
                  ⏳ Mettre en attente paiement
                </button>
              )}

              {/* Marquer payée */}
              {['validee', 'en_attente_paiement'].includes(selected.statut) && (
                <button onClick={() => changerStatut(selected.id_facture_achat, 'payee')}
                  disabled={actionLoading}
                  style={{ ...S.btn, background: theme.successBg, color: theme.success, fontSize: '13px', padding: '10px 18px' }}>
                  💚 Marquer comme payée
                </button>
              )}

              {/* Rejeter — avec warning confirm */}
              {!['rejetee', 'payee'].includes(selected.statut) && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', flex: 1, minWidth: isMobile ? '0' : '260px' }}>
                  <input
                    value={motifRejet}
                    onChange={e => setMotifRejet(e.target.value)}
                    placeholder="Motif de rejet (optionnel)…"
                    style={{
                      flex: '1 1 160px', minWidth: '0', padding: '8px 12px',
                      border: `1px solid ${theme.border}`, borderRadius: '8px', fontSize: '12px',
                    }}
                  />
                  <button
                    onClick={() => {
                      const ok = window.confirm(
                        `⚠️ Rejeter la facture de ${selected.nom_fournisseur} (${eur(selected.montant_ttc)} €) ?\n\n` +
                        `Cette action est réversible : vous pourrez la remettre en statut "Reçue" en cas d'erreur.`
                      );
                      if (ok) changerStatut(selected.id_facture_achat, 'rejetee', motifRejet);
                    }}
                    disabled={actionLoading}
                    style={{ ...S.btn, background: theme.dangerBg, color: theme.danger, fontSize: '13px', padding: '10px 14px', whiteSpace: 'nowrap' }}>
                    ❌ Rejeter
                  </button>
                </div>
              )}

              {/* Annuler le rejet — retour à "Reçue" (non disponible pour tombstones) */}
              {selected.statut === 'rejetee' && !selected.parse_error?.startsWith('TOMBSTONE:') && (
                <button
                  onClick={() => changerStatut(selected.id_facture_achat, 'recue')}
                  disabled={actionLoading}
                  style={{ ...S.btn, background: '#fff8e1', color: '#e65100', fontSize: '13px', padding: '10px 16px', whiteSpace: 'nowrap' }}>
                  ↩ Annuler le rejet
                </button>
              )}

              {/* Documents */}
              {selected.xml_brut && (
                <button
                  onClick={() => downloadXML(selected.id_facture_achat, selected.nom_fournisseur)}
                  style={{ ...S.btn, background: '#e8eaf6', color: '#1a237e', fontSize: '12px', padding: '8px 14px', marginLeft: 'auto' }}>
                  📄 XML
                </button>
              )}

              {/* Supprimer */}
              {!['payee'].includes(selected.statut) && (
                <button onClick={() => supprimer(selected.id_facture_achat)}
                  style={{ ...S.btn, background: '#fafafa', color: theme.textMuted, fontSize: '12px', padding: '10px 14px' }}>
                  🗑️
                </button>
              )}
            </div>
            )}

            {actionLoading && (
              <div style={{ textAlign: 'center', padding: '10px', color: theme.textMuted, fontSize: '13px' }}>
                ⏳ Action en cours…
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Modal import manuel ──────────────────────────────────────────────── */}
      {showImportModal && (
        <div style={S.overlay} onClick={(e) => { if (e.target === e.currentTarget) setShowImportModal(false); }}>
          <div style={{ ...S.modal, maxWidth: '560px' }} onClick={(e) => e.stopPropagation()}>

            {/* En-tête */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px' }}>
              <div>
                <div style={{ fontWeight: '700', fontSize: '17px', color: '#1a237e' }}>📥 Import manuel Peppyrus</div>
                <div style={{ fontSize: '12px', color: theme.textMuted, marginTop: '4px' }}>
                  Parcourez l'inbox ou entrez l'UUID directement
                </div>
              </div>
              <button onClick={() => setShowImportModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#666', lineHeight: 1 }}>
                ×
              </button>
            </div>

            {/* Résultat import */}
            {importResult && (
              <div style={{
                padding: '10px 14px', borderRadius: '8px', marginBottom: '14px',
                background: importResult.success ? '#e8f5e9' : '#fdecea',
                color: importResult.success ? '#2e7d32' : '#c62828',
                fontSize: '13px', fontWeight: '600',
              }}>
                {importResult.success ? '✅ ' : '❌ '}{importResult.message}
              </div>
            )}

            {/* ── Section 1 : Parcourir inbox Peppyrus ── */}
            <div style={{ marginBottom: '18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontWeight: '600', fontSize: '13px', color: '#333' }}>
                  Messages disponibles dans Peppyrus
                </div>
                <button
                  onClick={chargerInboxPeppyrus}
                  disabled={inboxLoading}
                  style={{ ...S.btn, padding: '6px 14px', fontSize: '12px',
                    background: '#1a237e', color: 'white',
                    cursor: inboxLoading ? 'wait' : 'pointer' }}
                >
                  {inboxLoading ? '⏳ Chargement…' : inboxPeppyrus === null ? '🔍 Parcourir' : '🔄 Actualiser'}
                </button>
              </div>

              {inboxPeppyrus !== null && (
                inboxPeppyrus.length === 0
                ? <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: '8px', fontSize: '13px', color: theme.textMuted, textAlign: 'center' }}>
                    Aucun message disponible dans l'inbox Peppyrus.<br/>
                    <span style={{ fontSize: '11px' }}>Si vous voyez la facture dans le portail web, elle a peut-être déjà été marquée comme lue lors d'un sync précédent — contactez le support Peppyrus/Tigron.</span>
                  </div>
                : <div style={{ maxHeight: '220px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                    {inboxPeppyrus.map((msg, i) => (
                      <div key={msg.id || i} style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '10px 12px',
                        borderBottom: i < inboxPeppyrus.length - 1 ? '1px solid #f0f0f0' : 'none',
                        background: msg.deja_importe ? '#f9f9f9' : 'white',
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: '600', fontSize: '13px', color: msg.deja_importe ? '#999' : '#1a237e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {msg.nom_fournisseur || msg.sender}
                          </div>
                          <div style={{ fontSize: '11px', color: theme.textMuted, display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '2px' }}>
                            {msg.numero_facture && <span>{msg.numero_facture}</span>}
                            {msg.montant_ttc    && <span style={{ fontWeight: '600', color: '#333' }}>{parseFloat(msg.montant_ttc).toLocaleString('fr-BE', { style: 'currency', currency: 'EUR' })}</span>}
                            {(msg.date_emission || msg.date) && <span>{new Date(msg.date_emission || msg.date).toLocaleDateString('fr-BE')}</span>}
                          </div>
                          <div style={{ fontSize: '10px', color: '#aaa', fontFamily: 'monospace', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {msg.id}
                          </div>
                        </div>
                        {msg.deja_importe
                          ? <span style={{ fontSize: '11px', color: '#999', whiteSpace: 'nowrap' }}>✓ déjà importé</span>
                          : <button
                              onClick={() => { setImportId(msg.id); setImportResult(null); importerParIdDirecte(msg.id); }}
                              disabled={importLoading}
                              style={{ ...S.btn, padding: '6px 12px', fontSize: '12px', whiteSpace: 'nowrap',
                                background: '#1a237e', color: 'white',
                                cursor: importLoading ? 'wait' : 'pointer' }}
                            >
                              📥 Importer
                            </button>
                        }
                      </div>
                    ))}
                  </div>
              )}
            </div>

            {/* ── Section 2 : UUID manuel ── */}
            <div style={{ borderTop: '1px solid #eee', paddingTop: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#333' }}>
                Ou entrez l'UUID directement
              </label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={importId}
                  onChange={(e) => { setImportId(e.target.value); setImportResult(null); }}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  style={{
                    flex: '1 1 220px', boxSizing: 'border-box',
                    padding: '10px 12px', borderRadius: '8px',
                    border: '1.5px solid #ccc', fontSize: '13px',
                    fontFamily: 'monospace',
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !importLoading) importerParId(); }}
                />
                <button
                  onClick={importerParId}
                  disabled={importLoading || !importId.trim()}
                  style={{
                    ...S.btn, padding: '10px 18px', fontSize: '13px',
                    background: importLoading || !importId.trim() ? '#ccc' : '#1a237e',
                    color: 'white', cursor: importLoading || !importId.trim() ? 'not-allowed' : 'pointer',
                  }}
                >
                  {importLoading ? '⏳…' : '📥 Importer'}
                </button>
              </div>
            </div>

            {/* Fermer */}
            <div style={{ marginTop: '16px', textAlign: 'right' }}>
              <button onClick={() => setShowImportModal(false)}
                style={{ ...S.btn, padding: '10px 20px', fontSize: '13px',
                  background: '#f0f0f0', color: '#333', cursor: 'pointer' }}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          MODAL — AJOUTER UNE FACTURE D'ACHAT MANUELLEMENT
      ══════════════════════════════════════════════════════════════════════ */}
      {showAddModal && (
        <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) setShowAddModal(false); }}>
          <div style={{ ...S.modal, maxWidth: '720px', padding: isMobile ? '16px' : '28px' }} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <div>
                <div style={{ fontWeight: '700', fontSize: '17px', color: '#1a237e' }}>➕ Ajouter une facture d'achat</div>
                <div style={{ fontSize: '12px', color: theme.textMuted, marginTop: '3px' }}>
                  Factures hors réseau Peppol — saisie manuelle ou upload PDF / image (OCR auto)
                </div>
              </div>
              <button onClick={() => setShowAddModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#666', lineHeight: 1 }}>
                ×
              </button>
            </div>

            {/* Toast */}
            {addToast && (
              <div style={{
                padding: '10px 14px', borderRadius: '8px', marginBottom: '14px', fontSize: '13px', fontWeight: '600',
                background: addToast.type === 'success' ? '#e8f5e9' : addToast.type === 'warning' ? '#fff8e1' : '#fdecea',
                color:      addToast.type === 'success' ? '#2e7d32' : addToast.type === 'warning' ? '#e65100' : '#c62828',
              }}>
                {addToast.msg}
              </div>
            )}

            {/* Onglets mode */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '18px', borderBottom: '1px solid #eee', paddingBottom: '14px' }}>
              {[
                { key: 'manuel', icon: '✏️', label: 'Saisie manuelle' },
                { key: 'ocr',    icon: '📷', label: 'Upload PDF / image (OCR auto)' },
              ].map(m => (
                <button key={m.key} onClick={() => { setAddMode(m.key); setAddPreview(null); setAddOcrText(''); }}
                  style={{
                    ...S.btn, padding: '8px 16px', fontSize: '13px',
                    background: addMode === m.key ? '#1a237e' : theme.primaryBg,
                    color:      addMode === m.key ? 'white'   : theme.textPrimary,
                    border:     addMode === m.key ? 'none' : `1px solid ${theme.border}`,
                  }}>
                  {m.icon} {m.label}
                </button>
              ))}
            </div>

            {/* Zone Upload + OCR */}
            {addMode === 'ocr' && (
              <div style={{ background: '#f8f9fa', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px' }}>
                  <button
                    onClick={() => addFileRef.current?.click()}
                    disabled={addOcrLoading}
                    style={{ ...S.btn, background: '#7c3aed', color: 'white', padding: '9px 18px', fontSize: '13px',
                      opacity: addOcrLoading ? 0.7 : 1, cursor: addOcrLoading ? 'wait' : 'pointer' }}>
                    {addOcrLoading ? '⏳ Analyse en cours…' : '📷 Sélectionner le fichier'}
                  </button>
                  <span style={{ fontSize: '11px', color: theme.textMuted }}>
                    JPG, PNG, WEBP, PDF · Max 15 Mo · Les champs sont pré-remplis automatiquement
                  </span>
                </div>
                <input ref={addFileRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,application/pdf"
                  style={{ display: 'none' }}
                  onChange={e => {
                    const f = e.target.files[0];
                    if (!f) return;
                    e.target.value = '';
                    handleOcrFacture(f);
                  }}
                />
                {addPreview && (
                  <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '12px', alignItems: 'flex-start', marginTop: '8px' }}>
                    <img src={addPreview} alt="Aperçu"
                      style={{ maxHeight: '140px', maxWidth: '180px', objectFit: 'contain',
                        borderRadius: '6px', border: '1px solid #ddd', background: 'white' }}
                      onError={e => { e.target.style.display='none'; }}
                    />
                    {addOcrText && (
                      <div style={{ flex: 1, background: 'white', borderRadius: '6px', border: '1px solid #ddd',
                        padding: '8px', fontSize: '10px', color: '#64748b', maxHeight: '140px', overflowY: 'auto' }}>
                        <strong style={{ display: 'block', marginBottom: '4px', fontSize: '11px' }}>Texte OCR brut :</strong>
                        {addOcrText.substring(0, 500)}{addOcrText.length > 500 ? '…' : ''}
                      </div>
                    )}
                    {addForm.ocr_status === 'done' && (
                      <span style={{ fontSize: '11px', fontWeight: '700', background: theme.successBg, color: theme.success,
                        borderRadius: '6px', padding: '4px 10px', whiteSpace: 'nowrap' }}>✅ OCR réussi</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── Formulaire ─────────────────────────────────────────────────── */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0 20px' }}>
              {/* Colonne gauche */}
              <div>
                <label style={{ fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>
                  Fournisseur *
                </label>
                <input value={addForm.nom_fournisseur}
                  onChange={e => setAddForm(p => ({ ...p, nom_fournisseur: e.target.value }))}
                  placeholder="Nom de la société fournisseur"
                  style={{ width: '100%', padding: '9px 12px', border: `1.5px solid ${!addForm.nom_fournisseur.trim() ? '#fca5a5' : '#ddd'}`,
                    borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box', marginBottom: '10px', outline: 'none' }} />

                <label style={{ fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>
                  N° TVA fournisseur
                </label>
                <input value={addForm.numero_tva_fournisseur}
                  onChange={e => setAddForm(p => ({ ...p, numero_tva_fournisseur: e.target.value }))}
                  placeholder="BE0123456789"
                  style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd',
                    borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box', marginBottom: '10px', outline: 'none' }} />

                <label style={{ fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>
                  N° Facture fournisseur
                </label>
                <input value={addForm.numero_facture_fournisseur}
                  onChange={e => setAddForm(p => ({ ...p, numero_facture_fournisseur: e.target.value }))}
                  placeholder="FACT-2026-001"
                  style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd',
                    borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box', marginBottom: '10px', outline: 'none' }} />

                <label style={{ fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>
                  IBAN fournisseur
                </label>
                <input value={addForm.iban_fournisseur}
                  onChange={e => setAddForm(p => ({ ...p, iban_fournisseur: e.target.value }))}
                  placeholder="BE12 3456 7890 1234"
                  style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd',
                    borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box', marginBottom: '10px', outline: 'none' }} />
              </div>

              {/* Colonne droite */}
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0 10px' }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Date émission</label>
                    <input type="date" value={addForm.date_emission}
                      onChange={e => { setAddForm(p => ({ ...p, date_emission: e.target.value })); checkTvaLock(e.target.value); }}
                      style={{ width: '100%', padding: '9px 10px', border: '1.5px solid #ddd', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box', marginBottom: '10px', outline: 'none' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Date échéance</label>
                    <input type="date" value={addForm.date_echeance}
                      onChange={e => setAddForm(p => ({ ...p, date_echeance: e.target.value }))}
                      style={{ width: '100%', padding: '9px 10px', border: '1.5px solid #ddd', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box', marginBottom: '10px', outline: 'none' }} />
                  </div>
                </div>

                {/* Avertissement TVA trimestre verrouillé */}
                {tvaLockWarning && (
                  <div style={{
                    marginBottom: 10,
                    padding: '10px 14px',
                    background: '#fff8e1',
                    border: '1px solid #f57f17',
                    borderRadius: 8,
                    fontSize: '12px',
                    color: '#333',
                    lineHeight: 1.6,
                  }}>
                    <strong>🔒 T{tvaLockWarning.trimestre} {tvaLockWarning.annee} est verrouillé</strong><br />
                    <span style={{ color: '#555' }}>
                      Cette facture apparaîtra dans le récap TVA T{tvaLockWarning.trimestre} {tvaLockWarning.annee} mais <strong>l'archive officielle (PDF/XML) est stale</strong>.<br />
                      Après l'enregistrement → <strong>TVA → 🔓 Déverrouiller T{tvaLockWarning.trimestre} {tvaLockWarning.annee} → Re-archiver</strong>.<br />
                      <span style={{ color: '#f57f17' }}>Achat oublié : légalement déductible dans le trimestre suivant (Art. 4 AR TVA), aucune rectificative Intervat nécessaire.</span>
                    </span>
                  </div>
                )}

                <label style={{ fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>
                  Montant TTC * (€)
                </label>
                <input type="number" step="0.01" min="0" value={addForm.montant_ttc}
                  onChange={e => {
                    const ttc = parseFloat(e.target.value || 0);
                    const { ht, tva } = calcFromTTC(ttc, addForm.taux_tva);
                    setAddForm(p => ({ ...p, montant_ttc: e.target.value, montant_ht: ht, montant_tva: tva }));
                  }}
                  style={{ width: '100%', padding: '9px 12px', border: `1.5px solid ${!addForm.montant_ttc ? '#fca5a5' : '#ddd'}`,
                    borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box', marginBottom: '10px', outline: 'none' }} />

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap: '0 10px' }}>
                  <div style={{ gridColumn: isMobile ? '1 / -1' : 'auto' }}>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Taux TVA</label>
                    <select value={addForm.taux_tva}
                      onChange={e => {
                        const ttc = parseFloat(addForm.montant_ttc || 0);
                        const { ht, tva } = calcFromTTC(ttc, e.target.value);
                        setAddForm(p => ({ ...p, taux_tva: e.target.value, montant_ht: ht, montant_tva: tva }));
                      }}
                      style={{ width: '100%', padding: '9px 8px', border: '1.5px solid #ddd', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box', marginBottom: '10px', outline: 'none', background: 'white' }}>
                      {['0','6','12','21'].map(t => <option key={t} value={t}>{t}%</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>HT (€)</label>
                    <input type="number" step="0.01" value={addForm.montant_ht} readOnly
                      style={{ width: '100%', padding: '9px 8px', border: '1.5px solid #ddd', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box', marginBottom: '10px', background: '#f9f9f9', outline: 'none' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>TVA (€)</label>
                    <input type="number" step="0.01" value={addForm.montant_tva} readOnly
                      style={{ width: '100%', padding: '9px 8px', border: '1.5px solid #ddd', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box', marginBottom: '10px', background: '#f9f9f9', outline: 'none' }} />
                  </div>
                </div>
              </div>
            </div>

            {/* ── Catégorie TVA belge ─────────────────────────────────────────── */}
            <div style={{ background: '#f0f4ff', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px' }}>
              <div style={{ fontWeight: '700', fontSize: '13px', color: '#1a237e', marginBottom: '10px' }}>
                🏛️ Catégorie TVA belge
                <span style={{ fontWeight: '400', fontSize: '11px', color: theme.textMuted, marginLeft: '8px' }}>
                  Détermine le % de TVA déductible en comptabilité
                </span>
              </div>
              {isMobile ? (
                <select
                  value={addForm.categorie_tva}
                  onChange={e => {
                    const key = e.target.value;
                    const cat = CATEGORIES_TVA[key] || CATEGORIES_TVA.standard;
                    setAddForm(p => ({ ...p, categorie_tva: key, tva_deductible_pct: cat.pct }));
                  }}
                  style={{
                    width: '100%', padding: '9px 12px', marginBottom: '10px',
                    border: `1.5px solid ${(CATEGORIES_TVA[addForm.categorie_tva] || CATEGORIES_TVA.standard).color}`,
                    borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box',
                    background: 'white', fontWeight: '600', outline: 'none',
                    color: (CATEGORIES_TVA[addForm.categorie_tva] || CATEGORIES_TVA.standard).color,
                  }}
                >
                  {Object.entries(CATEGORIES_TVA).map(([key, cat]) => (
                    <option key={key} value={key}>{cat.icon} {cat.label} — {cat.loi}</option>
                  ))}
                </select>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                  {Object.entries(CATEGORIES_TVA).map(([key, cat]) => (
                    <button key={key}
                      onClick={() => setAddForm(p => ({ ...p, categorie_tva: key, tva_deductible_pct: cat.pct }))}
                      style={{
                        padding: '5px 11px', borderRadius: '20px', border: '1.5px solid',
                        borderColor: addForm.categorie_tva === key ? cat.color : '#ddd',
                        background:  addForm.categorie_tva === key ? cat.bg   : 'white',
                        color:       addForm.categorie_tva === key ? cat.color : '#555',
                        fontSize: '12px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
                      }}
                      title={cat.loi}
                    >
                      {cat.icon} {cat.label}
                    </button>
                  ))}
                </div>
              )}
              {(() => {
                const cat = CATEGORIES_TVA[addForm.categorie_tva] || CATEGORIES_TVA.standard;
                return (
                  <div style={{ fontSize: '12px', color: cat.color, fontWeight: '700' }}>
                    → TVA déductible : {addForm.tva_deductible_pct}%
                    <span style={{ fontWeight: '400', color: '#888', marginLeft: '8px' }}>{cat.loi}</span>
                  </div>
                );
              })()}
            </div>

            {/* ── Fiscal IS/IPP — Déduction majorée 120% ─────────────────── */}
            {isMobile ? (
              /* Mobile : inline layout — pas de flex pour éviter tout débordement horizontal */
              <div style={{ marginBottom: '14px', paddingTop: '6px', paddingBottom: '2px' }}>
                <label style={{ display: 'block', cursor: 'pointer' }}>
                  <input type="checkbox"
                    checked={!!addForm.deduction_majoree_120}
                    onChange={e => setAddForm(p => ({ ...p, deduction_majoree_120: e.target.checked }))}
                    style={{ accentColor: '#6a1b9a', width: '16px', height: '16px', verticalAlign: 'middle', cursor: 'pointer', marginRight: '8px' }} />
                  <span style={{ fontSize: '13px', color: '#6a1b9a', fontWeight: '600', verticalAlign: 'middle' }}>
                    💼 Déduction 120% IS/IPP
                  </span>
                </label>
                <div style={{ fontSize: '11px', color: '#888', marginTop: '4px', paddingLeft: '24px' }}>
                  Art. 64ter CIR92 — ≠ TVA
                </div>
              </div>
            ) : (
              /* Desktop : bloc explicatif complet */
              <div style={{ background: '#f3e5f5', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px', border: '1px solid #ce93d8' }}>
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ fontWeight: '700', fontSize: '13px', color: '#6a1b9a' }}>💼 Impôt des Sociétés / IPP</div>
                  <div style={{ fontWeight: '400', fontSize: '11px', color: '#888', marginTop: '2px' }}>Distinct de la TVA — information pour votre comptable</div>
                </div>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                  <input type="checkbox"
                    checked={!!addForm.deduction_majoree_120}
                    onChange={e => setAddForm(p => ({ ...p, deduction_majoree_120: e.target.checked }))}
                    style={{ marginTop: '2px', accentColor: '#6a1b9a', width: '16px', height: '16px', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: '600', fontSize: '13px', color: '#6a1b9a' }}>Déduction majorée 120% (Art. 64ter CIR92)</div>
                    <div style={{ fontSize: '11px', color: '#888', marginTop: '2px', lineHeight: 1.5 }}>
                      Cybersécurité · IA/Digital — base IS/IPP à 120% du coût HT.{' '}
                      <strong style={{ color: '#6a1b9a' }}>≠ TVA</strong> (TVA déductible : {addForm.tva_deductible_pct}%)
                    </div>
                  </div>
                </label>
              </div>
            )}

            {/* Notes admin */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>
                Notes (optionnel)
              </label>
              <textarea value={addForm.notes_admin}
                onChange={e => setAddForm(p => ({ ...p, notes_admin: e.target.value }))}
                placeholder="Remarques internes, contexte de la dépense…"
                rows={2}
                style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '8px',
                  fontSize: '13px', boxSizing: 'border-box', resize: 'vertical', outline: 'none', fontFamily: 'system-ui,sans-serif' }} />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: isMobile ? 'stretch' : 'flex-end', gap: '10px', paddingTop: '4px' }}>
              <button onClick={() => setShowAddModal(false)}
                style={{ ...S.btn, padding: '10px 22px', fontSize: '13px', background: '#f0f0f0', color: '#333',
                  flex: isMobile ? '1' : 'none', justifyContent: 'center' }}>
                Annuler
              </button>
              <button onClick={saveManual} disabled={addSaving}
                style={{ ...S.btn, padding: '10px 22px', fontSize: '13px',
                  background: addSaving ? '#ccc' : '#1a237e', color: 'white',
                  cursor: addSaving ? 'wait' : 'pointer', fontWeight: '700',
                  flex: isMobile ? '1' : 'none', justifyContent: 'center' }}>
                {addSaving ? '⏳ Enregistrement…' : '💾 Enregistrer la facture'}
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default FacturesPro;
